import {forwardRef, useEffect, useId, useLayoutEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'

import {EnrichIcon, HistoryIcon, PolishIcon, SendIcon, ShortenIcon, WandIcon} from './icons'
import {MAX_REFINE_INPUT_LENGTH, MIN_REFINE_WORDS, REFINE_MODES, roomToEnrich} from './hooks/useRefineStory'
import {acquireScrollLock, scrollersBehind} from './hooks/useScrollLock'

export const REFINE_OPTIONS = [
  {
    mode: REFINE_MODES.POLISH,
    title: 'Polish',
    description: 'Fix grammar and smooth the flow. Keeps your words and voice.',
    Icon: PolishIcon
  },
  {
    mode: REFINE_MODES.SHORTEN,
    title: 'Shorten',
    description: 'Tighten it up while keeping what matters most.',
    Icon: ShortenIcon
  },
  {
    mode: REFINE_MODES.ENRICH,
    title: 'Enrich',
    description: 'Answer a few quick questions your family would love answered. Then we\u2019ll write it all into your story.',
    Icon: EnrichIcon
  }
]

export const CUSTOM_IDEAS = ['Make it warmer', 'Put it in the order it happened', 'Use simpler words']

export const MAX_INSTRUCTION_LENGTH = 300

const TOO_SHORT_NOTICE = 'Write a few more sentences first \u2014 there\u2019s not quite enough here to refine yet.'
const TOO_LONG_NOTICE = 'This story is long \u2014 select the part you\u2019d like to refine.'
const NO_ROOM_TO_ENRICH = 'Your story is at its length limit, so there isn\u2019t room to enrich it. Shorten it first.'
const ENRICH_TOO_LONG = 'This story is too long to enrich in one go. Shorten it first.'
const ENRICH_WHOLE_STORY = 'Reads your whole story, not just the selection.'

// What the arrow keys move between: the three options and the custom field.
const OPTION_SELECTOR = '.ls-refine-item:not(:disabled), textarea:not(:disabled)'
// Everything Tab can land on inside the sheet, for the phone focus cycle.
const FOCUSABLE_SELECTOR = 'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Below this width the menu is a bottom sheet (see lifeStory.scss), so it never flips.
// Phones: the menu is a bottom sheet and the Enrich interview a full-screen
// takeover (AC 2.7); the same breakpoint as the stylesheet.
export const SHEET_MEDIA_QUERY = '(max-width: 600px)'

// Breathing room the popover keeps from the bottom of the viewport before it flips,
// and above the wand for it to fit when flipped.
const FLIP_BOTTOM_MARGIN = 12
const FLIP_TOP_MARGIN = 20

export const isSheetLayout = () => typeof window.matchMedia === 'function' && window.matchMedia(SHEET_MEDIA_QUERY).matches

/**
 * Whether the open menu is the phone bottom sheet. Tracked from the media
 * query itself (not sampled once per render), so the modal semantics and
 * scroll lock follow the responsive layout when the viewport crosses the
 * breakpoint while the menu is open.
 */
const useSheetLayout = open => {
  const [sheet, setSheet] = useState(() => open && isSheetLayout())

  useEffect(() => {
    if (!open || typeof window.matchMedia !== 'function') {
      setSheet(false)

      return undefined
    }

    const query = window.matchMedia(SHEET_MEDIA_QUERY)
    const sync = () => setSheet(query.matches)

    sync()

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', sync)

      return () => query.removeEventListener('change', sync)
    }

    return undefined
  }, [open])

  return sheet
}

/**
 * While the phone sheet is open nothing behind its scrim may scroll: the
 * body and the flow's own scroll container (`.life-story-flow` is
 * `overflow-y: auto`), which a swipe or wheel over the scrim would
 * otherwise still move. Restored when the sheet closes or the layout
 * grows back into the desktop popover. The hold is shared with the other
 * layers' `useScrollLock`, so choosing Enrich — which closes the sheet and
 * mounts the takeover in one update — keeps the page locked throughout and
 * releases it exactly once.
 */
const useSheetScrollLock = (locked, rootRef) => {
  useEffect(() => (locked ? acquireScrollLock(scrollersBehind(rootRef.current)) : undefined), [locked, rootRef])
}

/**
 * Whether the popover, opened downward, would run off the bottom of the
 * viewport while there is room for it above the wand instead.
 */
export const shouldFlipMenu = ({menuRect, wandRect, viewportHeight}) => (
  menuRect.bottom > viewportHeight - FLIP_BOTTOM_MARGIN &&
    wandRect.top - menuRect.height - FLIP_TOP_MARGIN > 0
)

/**
 * The wand button and its "Refine" popover (TK-3204 sections 1–2; named
 * "Help me refine" until TK-3234 shortened it).
 *
 * The popover is a non-modal dialog rather than an ARIA menu: besides the
 * three options it holds a labelled text field, a send button and idea
 * chips, which a `menu` role can't own. Arrow keys still walk the options
 * and the field, and Escape closes from anywhere inside or from the wand.
 *
 * `onWandPointerDown` fires before the wand takes focus, while the textarea
 * still holds its selection, so the screen can capture it (AC 2.5).
 *
 * `selection` is `{words, length}` when the writer had three or more words selected
 * as they opened the menu; the scope line says so and the too-short check is
 * skipped. `textLength` and `wordCount` describe the whole answer so the
 * menu can refuse too little (AC 2.6) or, past the API's input cap, offer
 * only selection scope.
 */
const RefineMenu = forwardRef(({open, onOpen, onClose, onChoose, onWandPointerDown, selection, wordCount, textLength, arrive}, wandRef) => {
  const menuId = useId()
  const titleId = `${menuId}-title`
  const tipId = `${menuId}-tip`
  const noticeId = `${menuId}-notice`
  const customId = `${menuId}-custom`
  const wrapRef = useRef(null)
  const menuRef = useRef(null)
  const customRef = useRef(null)
  const [instruction, setInstruction] = useState('')
  const [flipped, setFlipped] = useState(false)
  // Phone width: the menu is a fixed bottom sheet behind a scrim, so it is
  // presented as modal (see `cycleTabInSheet`) and the page behind is locked.
  const sheet = useSheetLayout(open)

  useSheetScrollLock(sheet, wrapRef)

  const tooShort = !selection && wordCount < MIN_REFINE_WORDS
  const tooLong = (selection ? selection.length : textLength) > MAX_REFINE_INPUT_LENGTH
  const disabled = tooShort || tooLong
  // Enrich always reads the whole story, whatever is selected, so its gates
  // are the whole story's: too few words (a selection doesn't lift it), too
  // long to send in one go, or no room to grow (the API would refuse it
  // anyway). The other options still work at the cap.
  // With no selection `tooShort` already disables and explains everything.
  const enrichTooShort = Boolean(selection) && wordCount < MIN_REFINE_WORDS
  const enrichTooLong = textLength > MAX_REFINE_INPUT_LENGTH
  const noRoomToEnrich = !enrichTooLong && !roomToEnrich({textLength})
  const enrichWithheld = enrichTooShort || enrichTooLong || noRoomToEnrich
  const enrichNotice = () => {
    if (enrichTooShort) return TOO_SHORT_NOTICE
    if (enrichTooLong) return ENRICH_TOO_LONG

    return NO_ROOM_TO_ENRICH
  }
  const canSend = !disabled && instruction.trim().length > 0

  // Outside click closes without moving focus; the field is cleared for the next visit.
  useEffect(() => {
    if (!open) {
      setInstruction('')

      return undefined
    }

    const handlePointerDown = event => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) onClose({returnFocus: false})
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [open, onClose])

  // Opens downward by default; flips above the wand when there isn't room
  // below (desktop popover only — the phone sheet is fixed to the bottom).
  // Measured before paint so the menu never flashes in the wrong place, and
  // again on resize while open.
  useLayoutEffect(() => {
    if (!open) {
      setFlipped(false)

      return undefined
    }

    const place = () => {
      const menu = menuRef.current
      const wand = wandRef && wandRef.current

      if (!menu || !wand || isSheetLayout()) {
        setFlipped(false)

        return
      }

      // Measure as if opened downward, whatever the current placement. The
      // class is put back by hand as well as through state, since React skips
      // the re-render when the answer hasn't changed.
      menu.classList.remove('ls-refine-menu--up')

      const flip = shouldFlipMenu({
        menuRect: menu.getBoundingClientRect(),
        wandRect: wand.getBoundingClientRect(),
        viewportHeight: window.innerHeight
      })

      menu.classList.toggle('ls-refine-menu--up', flip)
      setFlipped(flip)
    }

    // The screen scrolls inside the fixed `.life-story-flow`, not the
    // window, so the wand's viewport position changes on that scroll too.
    const flow = wrapRef.current && wrapRef.current.closest('.life-story-flow')

    place()
    window.addEventListener('resize', place)
    if (flow) flow.addEventListener('scroll', place, {passive: true})

    return () => {
      window.removeEventListener('resize', place)
      if (flow) flow.removeEventListener('scroll', place)
    }
  }, [open, wandRef])

  // Opening lands focus on the first usable option (AC 9.9). When everything
  // is disabled the dialog itself takes focus, so keyboard and screen-reader
  // users still arrive inside it and hear the notice that explains why.
  // Becoming the modal sheet while open (a resize across the breakpoint)
  // pulls focus back in too: the desktop popover let Tab wander onto the
  // page, and behind the scrim that focus would be out of the sheet's reach.
  // Focus already inside the dialog is never moved — a breakpoint change
  // must not interrupt typing in the custom field.
  useEffect(() => {
    if (!open || !menuRef.current) return
    if (menuRef.current.contains(document.activeElement)) return

    const target = menuRef.current.querySelector(OPTION_SELECTOR) || menuRef.current

    target.focus()
  }, [open, sheet])

  const focusables = () => (menuRef.current ? Array.from(menuRef.current.querySelectorAll(OPTION_SELECTOR)) : [])

  const closeOnEscape = event => {
    if (event.key !== 'Escape') return false

    event.preventDefault()
    event.stopPropagation()
    onClose({returnFocus: true})

    return true
  }

  const handleWandKeyDown = event => {
    if (open) closeOnEscape(event)
  }

  // On phones the sheet is modal — a scrim covers the page — so Tab cycles
  // inside it rather than reaching the page's controls behind the scrim.
  // The desktop popover stays non-modal: Tab moves on through the page.
  const cycleTabInSheet = event => {
    if (event.key !== 'Tab' || !menuRef.current || !sheet) return false

    const list = Array.from(menuRef.current.querySelectorAll(FOCUSABLE_SELECTOR))

    // Everything disabled (too short / too long): the dialog itself holds
    // focus so Tab can't slip out to the controls behind the scrim.
    if (!list.length) {
      event.preventDefault()
      menuRef.current.focus()

      return true
    }

    const first = list[0]
    const last = list[list.length - 1]

    if (event.shiftKey && (document.activeElement === first || document.activeElement === menuRef.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }

    return true
  }

  const handleMenuKeyDown = event => {
    const inCustomField = event.target === customRef.current

    if (closeOnEscape(event) || cycleTabInSheet(event)) return

    if (inCustomField || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return

    event.preventDefault()

    const list = focusables()
    const index = list.indexOf(document.activeElement)
    const next = event.key === 'ArrowDown' ? list[index + 1] || list[0] : list[index - 1] || list[list.length - 1]

    if (next) next.focus()
  }

  const choose = mode => {
    onClose({returnFocus: false})
    onChoose({mode, instruction: ''})
  }

  const submitCustom = () => {
    const value = instruction.trim()

    if (!value || disabled) return

    onClose({returnFocus: false})
    setInstruction('')
    onChoose({mode: REFINE_MODES.CUSTOM, instruction: value.slice(0, MAX_INSTRUCTION_LENGTH)})
  }

  // Enter sends; Shift+Enter is a line break, and Enter confirming an IME
  // composition (East Asian input) is not a send.
  const handleCustomKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      submitCustom()
    }
  }

  const applyIdea = idea => {
    setInstruction(idea)

    if (customRef.current) customRef.current.focus()
  }

  return (
    <div className={`ls-wand-wrap${open ? ' open' : ''}`} ref={wrapRef}>
      <button
        aria-controls={menuId}
        aria-describedby={tipId}
        aria-expanded={open}
        aria-haspopup='dialog'
        aria-label='Refine'
        className={`ls-icon-btn ls-wand-btn${arrive ? ' arrive' : ''}`}
        ref={wandRef}
        type='button'
        onClick={() => (open ? onClose({returnFocus: true}) : onOpen())}
        onKeyDown={handleWandKeyDown}
        onPointerDown={onWandPointerDown}>
        <WandIcon />
      </button>
      <span className='ls-tip' id={tipId} role='tooltip'>Refine</span>
      {
        open &&
          <>
            <div aria-hidden='true' className='ls-refine-scrim' onClick={() => onClose({returnFocus: true})} />
            <div
              aria-describedby={disabled ? noticeId : undefined}
              aria-labelledby={titleId}
              aria-modal={sheet || undefined}
              className={`ls-refine-menu${flipped ? ' ls-refine-menu--up' : ''}`}
              id={menuId}
              ref={menuRef}
              role='dialog'
              tabIndex={-1}
              onKeyDown={handleMenuKeyDown}>
              <div className='ls-refine-menu-head'>
                <h3 id={titleId}>Refine</h3>
                <span className='ls-refine-scope'>
                  {selection ? <>Only your <b>selection</b> &middot; {selection.words} words</> : 'Your whole story'}
                </span>
              </div>
              {disabled && <div className='ls-refine-notice' id={noticeId}>{tooShort ? TOO_SHORT_NOTICE : TOO_LONG_NOTICE}</div>}
              {
                REFINE_OPTIONS.map(({mode, title, description, Icon}) => {
                  const enrich = mode === REFINE_MODES.ENRICH
                  const withheld = enrich && enrichWithheld
                  const desc = (() => {
                    if (!withheld) return enrich && selection ? `${description} ${ENRICH_WHOLE_STORY}` : description

                    return enrichNotice()
                  })()

                  return (
                    <button className='ls-refine-item' disabled={disabled || withheld} key={mode} type='button' onClick={() => choose(mode)}>
                      <span aria-hidden='true' className='ls-refine-item-icon'><Icon /></span>
                      <span>
                        <span className='ls-refine-item-title'>{title}</span>
                        <span className='ls-refine-item-desc'>{desc}</span>
                      </span>
                    </button>
                  )
                })
              }
              <div aria-hidden='true' className='ls-refine-sep' />
              <div className='ls-refine-custom'>
                <label htmlFor={customId}>Or describe the change you want</label>
                <div className='ls-refine-custom-row'>
                  <textarea
                    disabled={disabled}
                    id={customId}
                    maxLength={MAX_INSTRUCTION_LENGTH}
                    placeholder='In your own words&hellip;'
                    ref={customRef}
                    rows={1}
                    value={instruction}
                    onChange={event => setInstruction(event.target.value)}
                    onKeyDown={handleCustomKeyDown}
                  />
                  <button aria-label='Refine with this request' className='ls-refine-send' disabled={!canSend} type='button' onClick={submitCustom}>
                    <SendIcon />
                  </button>
                </div>
                <div className='ls-refine-ideas'>
                  {
                    CUSTOM_IDEAS.map(idea => (
                      <button className='ls-refine-idea' disabled={disabled} key={idea} type='button' onClick={() => applyIdea(idea)}>
                        {idea}
                      </button>
                    ))
                  }
                </div>
              </div>
              <div className='ls-refine-menu-foot'>
                <HistoryIcon />
                <span>AI won&rsquo;t add facts you didn&rsquo;t share, and you can always undo.</span>
              </div>
            </div>
          </>
      }
    </div>
  )
})

RefineMenu.displayName = 'RefineMenu'

RefineMenu.propTypes = {
  arrive: PropTypes.bool,
  onChoose: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onWandPointerDown: PropTypes.func,
  open: PropTypes.bool,
  selection: PropTypes.shape({length: PropTypes.number, words: PropTypes.number}),
  textLength: PropTypes.number,
  wordCount: PropTypes.number
}

export default RefineMenu
